import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import lamejs from '@breezystack/lamejs';
import QuestionRenderer from '../components/chat/QuestionRenderer';
import client from '../api/client';
import { useChat } from '../hooks/useChat';
import { useSurvey } from '../hooks/useSurvey';
import '../App.css';

function encodeMp3(samples, sampleRate) {
  const mp3enc = new lamejs.Mp3Encoder(1, sampleRate, 128);
  const blockSize = 1152;
  const int16 = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  const parts = [];
  for (let i = 0; i < int16.length; i += blockSize) {
    const buf = mp3enc.encodeBuffer(int16.subarray(i, i + blockSize));
    if (buf.length > 0) parts.push(buf);
  }
  const end = mp3enc.flush();
  if (end.length > 0) parts.push(end);
  return new Blob(parts, { type: 'audio/mp3' });
}

function Chatbot({ surveyId = '', sessionToken = '' }) {
  const token = useMemo(() => {
    if (sessionToken) return sessionToken;
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || '';
  }, [sessionToken]);

  const [draft, setDraft] = useState('');
  const [consentRefused, setConsentRefused] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [audioPreview, setAudioPreview] = useState(null); // { blob, url }
  const audioContextRef = useRef(null);
  const audioSourceRef = useRef(null);
  const audioProcessorRef = useRef(null);
  const audioStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const { messages, sendMessage, isLoading, setMessages } = useChat(token, surveyId);
  const { survey, progress, currentQuestion, isComplete, submitAnswer, applyAnswerResult, goPrevious } = useSurvey(token, surveyId);
  const messagesEndRef = useRef(null);

  // Persist token per survey in localStorage
  useEffect(() => {
    if (token && surveyId) {
      try {
        const sessions = JSON.parse(localStorage.getItem('immersion_sessions') || '{}');
        sessions[surveyId] = token;
        localStorage.setItem('immersion_sessions', JSON.stringify(sessions));
      } catch { /* ignore */ }
    }
  }, [token, surveyId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const [showCompleteModal, setShowCompleteModal] = useState(false);

  useEffect(() => {
    if (isComplete) {
      setShowCompleteModal(true);
    }
  }, [isComplete]);

  const onSend = async (event) => {
    event.preventDefault();
    if (!draft.trim() || consentRefused) return;
    await sendMessage(draft);
    setDraft('');
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      });
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (ctx.state === 'suspended') await ctx.resume();

      const source = ctx.createMediaStreamSource(stream);
      const processor = ctx.createScriptProcessor(4096, 1, 1);
      audioChunksRef.current = [];

      processor.onaudioprocess = (e) => {
        audioChunksRef.current.push(Float32Array.from(e.inputBuffer.getChannelData(0)));
      };

      source.connect(processor);
      processor.connect(ctx.destination);

      audioContextRef.current = ctx;
      audioSourceRef.current = source;
      audioProcessorRef.current = processor;
      audioStreamRef.current = stream;
      setIsRecording(true);
    } catch { /* mic denied */ }
  }, []);

  const stopRecording = useCallback(() => {
    if (audioProcessorRef.current) {
      audioProcessorRef.current.disconnect();
      audioProcessorRef.current.onaudioprocess = null;
      audioProcessorRef.current = null;
    }
    if (audioSourceRef.current) {
      audioSourceRef.current.disconnect();
      audioSourceRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }

    const sampleRate = audioContextRef.current?.sampleRate || 44100;
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    const totalLength = audioChunksRef.current.reduce((acc, c) => acc + c.length, 0);
    const merged = new Float32Array(totalLength);
    let offset = 0;
    for (const chunk of audioChunksRef.current) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    audioChunksRef.current = [];

    const blob = encodeMp3(merged, sampleRate);
    const url = URL.createObjectURL(blob);
    setIsRecording(false);
    setAudioPreview({ blob, url });
  }, []);

  const addBotMessage = useCallback((text) => {
    setMessages((prev) => [
      ...prev,
      { id: `b-${Date.now()}`, role: 'bot', message: text },
    ]);
  }, [setMessages]);

  const discardAudio = useCallback(() => {
    if (audioPreview?.url) URL.revokeObjectURL(audioPreview.url);
    setAudioPreview(null);
  }, [audioPreview]);

  const sendAudio = useCallback(async () => {
    if (!audioPreview?.blob) return;
    const blob = audioPreview.blob;
    const audioUrl = audioPreview.url;
    setAudioPreview(null);

    // Show audio as user message immediately + bot typing
    setMessages((prev) => [
      ...prev,
      { id: `u-${Date.now()}`, role: 'user', message: null, audioUrl },
    ]);
    setIsTranscribing(true);

    try {
      // Transcribe
      const formData = new FormData();
      formData.append('token', token);
      if (surveyId) formData.append('survey_id', surveyId);
      formData.append('audio', blob, 'chat.mp3');

      const res = await client.post('/chatbot/chat/audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });

      const rawText = res.data?.data?.text;
      const text = rawText ? rawText.replace(/SPEAKER\s*[A-Z]:\s*/gi, '').trim() : null;

      if (text) {
        // Send to Claude silently (no user bubble, just bot response)
        setIsTranscribing(false);
        await sendMessage(text, { silent: true });
      } else {
        setIsTranscribing(false);
        addBotMessage("D\u00e9sol\u00e9, je n'ai pas pu comprendre ton audio. Essaie encore ou tape ton message.");
      }
    } catch {
      setIsTranscribing(false);
      addBotMessage("D\u00e9sol\u00e9, une erreur est survenue. Tape ton message \u00e0 la place.");
    }
  }, [audioPreview, token, surveyId, sendMessage, setMessages, addBotMessage]);

  // addBotMessage moved above sendAudio

  const showQuestion = currentQuestion && !consentRefused;

  return (
    <div className="chat">
      {/* ── Header ─────────────────────────────── */}
      <header className="chat__header">
        <div className="chat__avatar">
          <svg className="chat__avatar-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
        </div>
        <div className="chat__header-info">
          <img
            src={`${process.env.PUBLIC_URL}/IMMERSION CONSULTING GROUP BLANC.png`}
            alt="Immersion Consulting Group"
            className="chat__header-logo"
          />
          <span className="chat__meta">
            {consentRefused
              ? 'Sondage termin\u00e9'
              : 'Assistant'}
          </span>
        </div>
      </header>

      {/* ── Progress ───────────────────────────── */}
      <div className="chat__progress">
        <div className="chat__progress-track">
          <div
            className="chat__progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* ── Messages ───────────────────────────── */}
      <div className="chat__messages">
        {messages.length === 0 && !isLoading && (
          <div className="chat__empty">
            <div className="chat__empty-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                <line x1="9" y1="10" x2="9" y2="10" />
                <line x1="12" y1="10" x2="12" y2="10" />
                <line x1="15" y1="10" x2="15" y2="10" />
              </svg>
            </div>
            <p className="chat__empty-text">Bienvenue !</p>
            <p className="chat__empty-hint">
              Tapez un message ci-dessous ou r&eacute;pondez &agrave; la question pour commencer.
            </p>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`chat__message chat__message--${message.role === 'user' ? 'user' : 'bot'}`}
          >
            <span className="chat__sender">
              {message.role === 'user' ? 'Vous' : 'Assistant'}
            </span>
            <div className="chat__bubble">
              {message.audioUrl ? (
                <VoicePlayer src={message.audioUrl} />
              ) : (
                message.message
              )}
            </div>
          </div>
        ))}

        {(isLoading || isTranscribing) && (
          <div className="chat__typing">
            <span className="chat__typing-dot" />
            <span className="chat__typing-dot" />
            <span className="chat__typing-dot" />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Question Panel ─────────────────────── */}
      {showQuestion && (
        <QuestionRenderer
          question={currentQuestion}
          onAnswer={submitAnswer}
          addBotMessage={addBotMessage}
          onSendMessage={sendMessage}
          onConsentRefused={() => setConsentRefused(true)}
          token={token}
          surveyId={surveyId}
          applyAnswerResult={applyAnswerResult}
          onPrevious={goPrevious}
        />
      )}

      {/* ── Input ──────────────────────────────── */}
      <div className="chat__input-area">
        {/* Recording state */}
        {isRecording && (
          <div className="chat__input-form">
            <div className="chat__recording">
              <span className="chat__recording-pulse" />
              <span className="chat__recording-text">{"Enregistrement\u2026"}</span>
            </div>
            <button type="button" className="chat__recording-stop" onClick={stopRecording}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <rect x="4" y="4" width="16" height="16" rx="2" />
              </svg>
            </button>
          </div>
        )}

        {/* Audio preview state */}
        {!isRecording && audioPreview && !isTranscribing && (
          <div className="chat__input-form">
            <audio src={audioPreview.url} controls className="chat__audio-preview" />
            <button type="button" className="chat__audio-discard" onClick={discardAudio}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <button type="button" className="chat__send-btn" onClick={sendAudio}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        )}

        {/* Normal input state */}
        {!isRecording && !audioPreview && (
          <form className="chat__input-form" onSubmit={onSend}>
            <input
              className="chat__input"
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={isLoading || consentRefused}
              placeholder={consentRefused
                ? 'Le sondage est termin\u00e9. Merci !'
                : 'Tapez votre message\u2026'}
            />
            <button
              type="button"
              className="chat__mic-btn"
              onClick={startRecording}
              disabled={isLoading || consentRefused}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            </button>
            <button
              type="submit"
              className="chat__send-btn"
              disabled={isLoading || !draft.trim() || consentRefused}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </form>
        )}
      </div>

      {/* ── Completion Modal ───────────────────── */}
      {showCompleteModal && (
        <div className="modal-overlay" onClick={() => { window.location.href = '/results'; }}>
          <div className="complete-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal__close"
              onClick={() => { window.location.href = '/results'; }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="complete-modal__icon">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            <h2 className="complete-modal__title">Sondage terminé</h2>
            <p className="complete-modal__subtitle">
              Merci pour votre participation ! Vos réponses ont bien été enregistrées.
            </p>

            <button
              type="button"
              className="btn btn--primary complete-modal__btn"
              onClick={() => { window.location.href = '/results'; }}
            >
              Voir les résultats
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Custom Voice Player ────────────────────────────── */
function VoicePlayer({ src }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoaded = () => setDuration(audio.duration || 0);
    const onTime = () => setCurrentTime(audio.currentTime || 0);
    const onEnded = () => setPlaying(false);

    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) { audio.pause(); } else { audio.play(); }
    setPlaying(!playing);
  };

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = pct * duration;
  };

  const fmt = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="voice-player">
      <audio ref={audioRef} src={src} preload="metadata" />

      <button type="button" className="voice-player__btn" onClick={toggle}>
        {playing ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16" rx="1" />
            <rect x="14" y="4" width="4" height="16" rx="1" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
        )}
      </button>

      <div className="voice-player__body">
        <div
          className="voice-player__track"
          onClick={seek}
          onMouseDown={(e) => {
            const track = e.currentTarget;
            const move = (ev) => {
              const rect = track.getBoundingClientRect();
              const pct = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
              if (audioRef.current && duration) audioRef.current.currentTime = pct * duration;
            };
            const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
            document.addEventListener('mousemove', move);
            document.addEventListener('mouseup', up);
          }}
          onTouchStart={(e) => {
            const track = e.currentTarget;
            const move = (ev) => {
              const touch = ev.touches[0];
              const rect = track.getBoundingClientRect();
              const pct = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
              if (audioRef.current && duration) audioRef.current.currentTime = pct * duration;
            };
            const end = () => { document.removeEventListener('touchmove', move); document.removeEventListener('touchend', end); };
            document.addEventListener('touchmove', move);
            document.addEventListener('touchend', end);
          }}
        >
          <div className="voice-player__fill" style={{ width: `${progress}%` }} />
          <span className="voice-player__thumb" style={{ left: `${progress}%` }} />
        </div>

        <span className="voice-player__time">
          {fmt(currentTime)}{' / '}{fmt(duration)}
        </span>
      </div>
    </div>
  );
}

export default Chatbot;
